import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { FileUploadService } from './file-upload.service';
import { AgremoImageryUploadUrl } from '../response-models/agremo-imagery-upload-url.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

/**
 * Agremo imagery uploads: `POST /file-uploads/agremo-imagery` returns a pre-signed S3 PUT URL
 * (hub `user-file-uploads-controller.ts`). The imagery is public-read so Agremo can fetch it directly.
 */
@Injectable({
  providedIn: 'root'
})
export class AgremoImageryUploadService extends ApiService {
  private readonly fileUploads = inject(FileUploadService);

  constructor() {
    super('file-uploads/agremo-imagery');
  }

  /** A pre-signed URL for uploading `filename`. Rejects with an `ApiError`. */
  getImageryUploadUrl(filename: string): Promise<AgremoImageryUploadUrl> {
    return firstValueFrom(this.request('POST', {}, toUploadUrl, { filename }));
  }

  /** PUT a file to S3 with a pre-signed URL (S3, not the hub, so no `ApiService` plumbing). */
  /** The hub signs imagery URLs `public-read` so Agremo can fetch the files directly. */
  async uploadToS3(file: File, uploadUrl: string): Promise<void> {
    await this.fileUploads.uploadToSignedUrl(uploadUrl, file, { publicRead: true });
  }

  /** Upload an imagery file and return where it landed. */
  async uploadImagery(file: File): Promise<{ s3Url: string; s3Key: string }> {
    const urlInfo = await this.getImageryUploadUrl(file.name);
    if (!urlInfo.uploadUrl) {
      throw new Error('Upload failed: no upload URL was returned');
    }
    await this.uploadToS3(file, urlInfo.uploadUrl);
    return { s3Url: urlInfo.s3Url, s3Key: urlInfo.s3Key };
  }
}

function toUploadUrl(data: unknown): AgremoImageryUploadUrl {
  const source = isRecord(data) ? data : {};
  return {
    uploadUrl: nullableString(source['uploadUrl']) ?? '',
    s3Key: nullableString(source['s3Key']) ?? '',
    s3Url: nullableString(source['s3Url']) ?? '',
    fileName: nullableString(source['fileName']) ?? '',
  };
}
